import React, { useState, useEffect } from "react";
import { useClickOutside } from "../../../utils";
import { FontFamily } from "@/utils/constants/fonts";
import {
  Plus,
  Minus,
  PaintBucket,
  Bold,
  Italic,
  Underline,
  Baseline,
  RotateCw,
  Blend,
  Eraser,
} from "lucide-react";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
interface TextEditingState {
  isEditing: boolean;
  textOverlayId?: string;
  text: string;
  fontSize: number;
  fontColor: string;
  backgroundColor: string;
  position: { x: number; y: number };
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
  padding?: number;
  rotation?: number; // Rotation in degrees
  opacity?: number; // Opacity (0-1)
  fontFamily?: string;
}

interface LogoEditingState {
  isEditing: boolean;
  logoOverlayId?: string;
  url: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  opacity: number;
  aspectRatio: number;
  rotation?: number; // Rotation in degrees
}

interface OverlayEditingModalProps {
  showModal: boolean;
  editingType: "text" | "logo" | "slate";
  textEditingState?: TextEditingState;
  logoEditingState?: LogoEditingState;
  videoDimensions?: { width: number; height: number };
  maintainAspectRatio?: boolean;
  onTextChange?: (text: string) => void;
  onFontSizeChange?: (fontSize: number) => void;
  onFontColorChange?: (fontColor: string) => void;
  onBackgroundColorChange?: (backgroundColor: string) => void;
  onFontFamilyChange?: (fontFamily: FontFamily) => void;
  onBoldChange?: (isBold: boolean) => void;
  onItalicChange?: (isItalic: boolean) => void;
  onUnderlineChange?: (isUnderline: boolean) => void;
  onPaddingChange?: (padding: number) => void;
  onPositionChange?: (position: { x: number; y: number }) => void;
  onSizeChange?: (size: { width: number; height: number }) => void;
  onOpacityChange?: (opacity: number) => void;
  onRotationChange?: (rotation: number) => void;
  onAspectRatioToggle?: (maintainAspectRatio: boolean) => void;
  onReset?: () => void;
  onCancel?: () => void;
  onSwitchToInlineEdit?: () => void; // New callback for switching to inline editing
  onRemove?: () => void; // New callback for removing the overlay
  onCloseSliders?: React.MutableRefObject<(() => void) | null>;
  disableClickOutside?: boolean;
}

export const OverlayEditingModal: React.FC<OverlayEditingModalProps> = ({
  showModal,
  editingType,
  textEditingState,
  logoEditingState,
  videoDimensions,
  maintainAspectRatio = false,
  onFontSizeChange,
  onFontColorChange,
  onBackgroundColorChange,
  onBoldChange,
  onItalicChange,
  onUnderlineChange,
  onPaddingChange,
  onPositionChange,
  onSizeChange,
  onOpacityChange,
  onRotationChange,
  onAspectRatioToggle,
  onCancel,
  onRemove,
  onCloseSliders,
  disableClickOutside = false,
}) => {
  const [showOpacitySlider, setShowOpacitySlider] = useState(false);
  const [showRotationSlider, setShowRotationSlider] = useState(false);

  const modalRef = React.useRef<HTMLDivElement>(null);
  const opacitySliderRef = React.useRef<HTMLDivElement>(null);
  const rotationSliderRef = React.useRef<HTMLDivElement>(null);

  useClickOutside(modalRef, () => {
    if (showModal && !disableClickOutside) {
      onCancel?.();
    }
  });

  useClickOutside(opacitySliderRef, () => {
    if (showOpacitySlider) {
      setShowOpacitySlider(false);
    }
  });
  useClickOutside(rotationSliderRef, () => {
    if (showRotationSlider) {
      setShowRotationSlider(false);
    }
  });

  React.useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as Element;
      const isCanvasClick = target.closest('[data-overlay-canvas="true"]');
      const isOutsideModal = !modalRef.current?.contains(target);
      if (isCanvasClick || isOutsideModal) {
        if (showOpacitySlider) {
          setShowOpacitySlider(false);
        }
        if (showRotationSlider) {
          setShowRotationSlider(false);
        }
      }
    };

    if (showOpacitySlider || showRotationSlider) {
      setTimeout(() => {
        document.addEventListener("click", handleGlobalClick);
      }, 100);
      return () => document.removeEventListener("click", handleGlobalClick);
    }
  }, [showOpacitySlider, showRotationSlider]);
  useEffect(() => {
    if (onCloseSliders) {
      onCloseSliders.current = () => {
        setShowOpacitySlider(false);
        setShowRotationSlider(false);
      };
    }
  }, [
    onCloseSliders,
    showModal,
    editingType,
    showOpacitySlider,
    showRotationSlider,
  ]);

  if (!showModal) {
    return null;
  }

  const handleWidthChange = (newWidth: number) => {
    if (!logoEditingState || !onSizeChange) return;

    if (
      isNaN(newWidth) ||
      newWidth === null ||
      newWidth === undefined ||
      newWidth < 0
    )
      return;

    if (
      maintainAspectRatio &&
      logoEditingState.aspectRatio &&
      isFinite(logoEditingState.aspectRatio) &&
      logoEditingState.aspectRatio > 0
    ) {
      const newHeight = newWidth / logoEditingState.aspectRatio;
      if (isFinite(newHeight) && newHeight >= 0) {
        onSizeChange({ width: newWidth, height: newHeight });
      }
    } else {
      onSizeChange({ ...logoEditingState.size, width: newWidth });
    }
  };

  const handleHeightChange = (newHeight: number) => {
    if (!logoEditingState || !onSizeChange) return;

    if (
      isNaN(newHeight) ||
      newHeight === null ||
      newHeight === undefined ||
      newHeight < 0
    )
      return;

    if (
      maintainAspectRatio &&
      logoEditingState.aspectRatio &&
      isFinite(logoEditingState.aspectRatio) &&
      logoEditingState.aspectRatio > 0
    ) {
      const newWidth = newHeight * logoEditingState.aspectRatio;
      if (isFinite(newWidth) && newWidth >= 0) {
        onSizeChange({ width: newWidth, height: newHeight });
      }
    } else {
      onSizeChange({ ...logoEditingState.size, height: newHeight });
    }
  };

  const renderTextControls = () => {
    if (editingType !== "text" || !textEditingState) return null;

    return (
      <>
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="fill-color-tooltip">Fill Color</Tooltip>}
        >
          <div
            className="position-relative colorCont"
            style={{ marginTop: "-6px" }}
          >
            <PaintBucket size={16} />
            <div
              className="colorContInner position-absolute end-0 start-0 w-100"
              style={{
                height: "4px",
                background:
                  textEditingState.backgroundColor === "transparent"
                    ? "transparent"
                    : textEditingState.backgroundColor,
              }}
            ></div>
            <input
              type="color"
              className="position-absolute top-0 start-0 w-100 opacity-0"
              value={
                textEditingState.backgroundColor === "transparent"
                  ? "#000000"
                  : textEditingState.backgroundColor
              }
              onChange={(e) => onBackgroundColorChange?.(e.target.value)}
              style={{ cursor: "pointer", height: "calc(100% + 4px)" }}
            />
          </div>
        </OverlayTrigger>

        <div className="d-flex align-items-center border-start border-secondary-subtle ps-3 ms-3 gap-2">
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="decrease-font-size-tooltip">
                Decrease Font Size
              </Tooltip>
            }
          >
            <button
              className="d-flex align-items-center textBtn"
              onClick={() =>
                onFontSizeChange?.(Math.max(8, textEditingState.fontSize - 1))
              }
            >
              <Minus size={18} />
            </button>
          </OverlayTrigger>
          <input
            type="number"
            className="form-control text-center border-secondary-subtle mx-1 bg-transparent"
            value={
              isNaN(textEditingState.fontSize) ? 24 : textEditingState.fontSize
            }
            onChange={(e) => {
              const newFontSize = Number(e.target.value);
              if (
                !isNaN(newFontSize) &&
                newFontSize !== null &&
                newFontSize !== undefined
              ) {
                onFontSizeChange?.(newFontSize);
              }
            }}
            style={{ width: "47px", height: "24px", fontSize: "12px" }}
            min="8"
            max="72"
          />
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="increase-font-size-tooltip">
                Increase Font Size
              </Tooltip>
            }
          >
            <button
              className="d-flex align-items-center textBtn f-30"
              onClick={() =>
                onFontSizeChange?.(Math.min(72, textEditingState.fontSize + 1))
              }
            >
              <Plus size={18} />
            </button>
          </OverlayTrigger>
        </div>

        {/* Text Formatting Buttons */}
        <div className="d-flex align-items-center border-start border-end border-secondary-subtle px-3 gap-2">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="bold-tooltip">Bold</Tooltip>}
          >
            <button
              className={`textBtn ${
                textEditingState.isBold
                  ? "btn-primary"
                  : "btn-outline-secondary"
              }`}
              onClick={() => onBoldChange?.(!textEditingState.isBold)}
            >
              <Bold size={16} />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="italic-tooltip">Italic</Tooltip>}
          >
            <button
              className={`textBtn ${
                textEditingState.isItalic
                  ? "btn-primary"
                  : "btn-outline-secondary"
              }`}
              onClick={() => onItalicChange?.(!textEditingState.isItalic)}
            >
              <Italic size={16} />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="underline-tooltip">Underline</Tooltip>}
          >
            <button
              className={`textBtn ${
                textEditingState.isUnderline
                  ? "btn-primary"
                  : "btn-outline-secondary"
              }`}
              onClick={() => onUnderlineChange?.(!textEditingState.isUnderline)}
            >
              <Underline size={16} />
            </button>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="text-color-tooltip">Text Color</Tooltip>}
          >
            <button
              className="textBtn position-relative"
              style={{ width: "28px", height: "28px", fontSize: "12px" }}
            >
              <Baseline size={16} />
              <input
                type="color"
                className="position-absolute top-0 start-0 w-100 h-100 opacity-0"
                value={textEditingState.fontColor}
                onChange={(e) => onFontColorChange?.(e.target.value)}
                style={{ cursor: "pointer" }}
              />
            </button>
          </OverlayTrigger>
        </div>

        {/* Padding Controls */}
        <div className="d-flex align-items-center border-end  border-secondary-subtle pe-3 me-3">
          <button
            className="d-flex align-items-center textBtn"
            onClick={() =>
              onPaddingChange?.(
                Math.max(0, (textEditingState.padding || 0) - 5)
              )
            }
            title="Decrease Padding"
          >
            <Minus size={18} />
          </button>
          <input
            type="number"
            className="form-control text-center border mx-1 bg-white"
            value={(() => {
              const padding = textEditingState.padding ?? 0;
              return isNaN(padding) ? 0 : padding;
            })()}
            onChange={(e) => {
              const newPadding = Number(e.target.value);
              if (
                !isNaN(newPadding) &&
                newPadding !== null &&
                newPadding !== undefined
              ) {
                onPaddingChange?.(newPadding);
              }
            }}
            style={{ width: "45px", height: "24px", fontSize: "12px" }}
            min="0"
            max="50"
          />
          <button
            className="d-flex align-items-center textBtn"
            onClick={() =>
              onPaddingChange?.(
                Math.min(50, (textEditingState.padding || 0) + 5)
              )
            }
            title="Increase Padding"
          >
            <Plus size={18} />
          </button>
        </div>

        <div className="position-relative">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="opacity-tooltip">Opacity</Tooltip>}
          >
            <button
              className="textBtn d-flex align-items-center justify-content-center"
              onClick={() => {
                setShowRotationSlider(false);
                setShowOpacitySlider(!showOpacitySlider);
              }}
              style={{ width: "28px", height: "28px" }}
            >
              <Blend size={16} />
            </button>
          </OverlayTrigger>
          {showOpacitySlider && (
            <div
              ref={opacitySliderRef}
              className="position-absolute bg-white border rounded p-2 shadow"
              style={{
                top: "100%",
                left: "0",
                zIndex: 1000,
                minWidth: "120px",
              }}
            >
              <div className="small fw-bold mb-1">Opacity</div>
              <input
                type="range"
                className="form-range"
                min="0"
                max="100"
                value={Math.round((textEditingState.opacity ?? 1) * 100)}
                onChange={(e) => {
                  const newOpacity = Number(e.target.value) / 100;
                  if (
                    !isNaN(newOpacity) &&
                    newOpacity !== null &&
                    newOpacity !== undefined
                  ) {
                    onOpacityChange?.(newOpacity);
                  }
                }}
                style={{ width: "100px", height: "18px" }}
              />
              <div className="small text-center mt-1">
                {Math.round((textEditingState.opacity ?? 1) * 100)}%
              </div>
            </div>
          )}
        </div>

        <div className="position-relative border-end border-secondary-subtle pe-3">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="rotation-tooltip">Rotation</Tooltip>}
          >
            <button
              className="textBtn d-flex align-items-center justify-content-center"
              onClick={() => {
                setShowOpacitySlider(false);
                setShowRotationSlider(!showRotationSlider);
              }}
              style={{ width: "28px", height: "28px", fontSize: "12px" }}
            >
              <RotateCw size={16} />
            </button>
          </OverlayTrigger>
          {showRotationSlider && (
            <div
              ref={rotationSliderRef}
              className="position-absolute bg-white border rounded p-2 shadow"
              style={{
                top: "100%",
                left: "0",
                zIndex: 1000,
                minWidth: "120px",
              }}
            >
              <div className="small fw-bold mb-1">Rotation</div>

              <div className="d-flex mb-2">
                <button
                  className={`btn border d-flex align-items-center justify-content-center ${
                    (textEditingState.rotation || 0) === 0
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => onRotationChange?.(0)}
                  style={{ width: "24px", height: "24px", fontSize: "10px" }}
                  title="0°"
                >
                  ↻
                </button>
                <button
                  className={`btn border d-flex align-items-center justify-content-center ${
                    (textEditingState.rotation || 0) === 90
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => onRotationChange?.(90)}
                  style={{ width: "24px", height: "24px", fontSize: "10px" }}
                  title="90°"
                >
                  <span style={{ transform: "rotate(90deg)" }}>↻</span>
                </button>
                <button
                  className={`btn border d-flex align-items-center justify-content-center ${
                    (textEditingState.rotation || 0) === 180
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => onRotationChange?.(180)}
                  style={{ width: "24px", height: "24px", fontSize: "10px" }}
                  title="180°"
                >
                  <span style={{ transform: "rotate(180deg)" }}>↻</span>
                </button>
                <button
                  className={`btn border d-flex align-items-center justify-content-center ${
                    (textEditingState.rotation || 0) === 270
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => onRotationChange?.(270)}
                  style={{ width: "24px", height: "24px", fontSize: "10px" }}
                  title="270°"
                >
                  <span style={{ transform: "rotate(270deg)" }}>↻</span>
                </button>
              </div>

              <input
                type="range"
                className="form-range"
                min="-360"
                max="360"
                value={textEditingState.rotation || 0}
                onChange={(e) => {
                  const newRotation = Number(e.target.value);
                  if (
                    !isNaN(newRotation) &&
                    newRotation !== null &&
                    newRotation !== undefined
                  ) {
                    onRotationChange?.(newRotation);
                  }
                }}
                style={{ width: "100px", height: "18px" }}
              />
              <div className="small text-center mt-1">
                {textEditingState.rotation || 0}°
              </div>
            </div>
          )}
        </div>
        <button
          className="btn btn-outline-danger btn-sm border-0 ms-auto d-flex align-items-center justify-content-center"
          onClick={onRemove}
          style={{ fontSize: "11px", whiteSpace: "nowrap" }}
          title="Remove text overlay"
        >
          <Eraser size={16} />
        </button>
      </>
    );
  };

  const renderLogoControls = () => {
    if (editingType !== "logo" || !logoEditingState) return null;

    const videoWidth = videoDimensions?.width || 1920;
    const videoHeight = videoDimensions?.height || 1080;

    const logoWidthPercent = (logoEditingState.size.width / videoWidth) * 100;
    const logoHeightPercent =
      (logoEditingState.size.height / videoHeight) * 100;

    const cornerX = Math.round(
      logoEditingState.position.x - logoWidthPercent / 2
    );
    const cornerY = Math.round(
      logoEditingState.position.y - logoHeightPercent / 2
    );

    const minX = 0;
    const maxX = Math.round(100 - logoWidthPercent);
    const minY = 0;
    const maxY = Math.round(100 - logoHeightPercent);
    return (
      <>
        <div className="d-flex align-items-center">
          <span className="small fw-bold me-1">X:</span>
          <input
            type="number"
            className="form-control text-center border bg-white"
            value={isNaN(cornerX) ? 0 : cornerX}
            onChange={(e) => {
              const newCornerX = Number(e.target.value);
              if (
                !isNaN(newCornerX) &&
                newCornerX !== null &&
                newCornerX !== undefined
              ) {
                const newCenterX = newCornerX + logoWidthPercent / 2;
                onPositionChange?.({
                  ...logoEditingState.position,
                  x: newCenterX,
                });
              }
            }}
            style={{ width: "60px", height: "18px", fontSize: "10px" }}
            min={Math.round(minX)}
            max={Math.round(maxX)}
            step="1"
            title={`Range: ${Math.round(minX)} - ${Math.round(maxX)}`}
          />
          <span className="small fw-bold mx-1">Y:</span>
          <input
            type="number"
            className="form-control text-center border bg-white"
            value={isNaN(cornerY) ? 0 : cornerY}
            onChange={(e) => {
              const newCornerY = Number(e.target.value);
              if (
                !isNaN(newCornerY) &&
                newCornerY !== null &&
                newCornerY !== undefined
              ) {
                const newCenterY = newCornerY + logoHeightPercent / 2;
                onPositionChange?.({
                  ...logoEditingState.position,
                  y: newCenterY,
                });
              }
            }}
            style={{ width: "60px", height: "18px", fontSize: "10px" }}
            min={Math.round(minY)}
            max={Math.round(maxY)}
            step="1"
            title={`Range: ${Math.round(minY)} - ${Math.round(maxY)}`}
          />
        </div>

        <div className="d-flex align-items-center">
          <span className="small fw-bold me-1">W:</span>
          <input
            type="number"
            className="form-control text-center border bg-white"
            value={
              isNaN(logoEditingState.size.width)
                ? 100
                : Math.round(logoEditingState.size.width)
            }
            onChange={(e) => handleWidthChange(Number(e.target.value))}
            style={{ width: "60px", height: "18px", fontSize: "10px" }}
            min="20"
            max="800"
            step="1"
          />
          <span className="small fw-bold mx-1">H:</span>
          <input
            type="number"
            className="form-control text-center border bg-white"
            value={
              isNaN(logoEditingState.size.height)
                ? 100
                : Math.round(logoEditingState.size.height)
            }
            onChange={(e) => handleHeightChange(Number(e.target.value))}
            style={{ width: "60px", height: "18px", fontSize: "10px" }}
            min="20"
            max="600"
            step="1"
          />
        </div>

        <div className="position-relative" ref={opacitySliderRef}>
          <button
            className="btn btn-outline-secondary border d-flex align-items-center justify-content-center bg-white"
            onClick={() => {
              setShowRotationSlider(false);
              setShowOpacitySlider(!showOpacitySlider);
            }}
            style={{ width: "28px", height: "28px", fontSize: "12px" }}
            title="Opacity"
          >
            <span style={{ opacity: 0.7 }}>●</span>
          </button>
          {showOpacitySlider && (
            <div
              className="position-absolute bg-white border rounded p-2 shadow"
              style={{
                top: "100%",
                left: "0",
                zIndex: 1000,
                minWidth: "120px",
              }}
            >
              <div className="small fw-bold mb-1">Opacity</div>
              <input
                type="range"
                className="form-range"
                min="0"
                max="100"
                value={Math.round(logoEditingState.opacity * 100)}
                onChange={(e) => {
                  const newOpacity = Number(e.target.value) / 100;
                  if (
                    !isNaN(newOpacity) &&
                    newOpacity !== null &&
                    newOpacity !== undefined
                  ) {
                    onOpacityChange?.(newOpacity);
                  }
                }}
                style={{ width: "100px", height: "18px" }}
              />
              <div className="small text-center mt-1">
                {Math.round(logoEditingState.opacity * 100)}%
              </div>
            </div>
          )}
        </div>

        <div className="position-relative">
          <button
            className="btn btn-outline-secondary border d-flex align-items-center justify-content-center bg-white"
            onClick={() => {
              setShowOpacitySlider(false);
              setShowRotationSlider(!showRotationSlider);
            }}
            style={{ width: "28px", height: "28px", fontSize: "12px" }}
            title="Rotation"
          >
            <span style={{ transform: "rotate(45deg)" }}>↻</span>
          </button>
          {showRotationSlider && (
            <div
              ref={rotationSliderRef}
              className="position-absolute bg-white border rounded p-2 shadow"
              style={{
                top: "100%",
                left: "0",
                zIndex: 1000,
                minWidth: "120px",
              }}
            >
              <div className="small fw-bold mb-1">Rotation</div>

              <div className="d-flex mb-2">
                <button
                  className={`btn border d-flex align-items-center justify-content-center ${
                    (logoEditingState.rotation || 0) === 0
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => onRotationChange?.(0)}
                  style={{ width: "24px", height: "24px", fontSize: "10px" }}
                  title="0°"
                >
                  ↻
                </button>
                <button
                  className={`btn border d-flex align-items-center justify-content-center ${
                    (logoEditingState.rotation || 0) === 90
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => onRotationChange?.(90)}
                  style={{ width: "24px", height: "24px", fontSize: "10px" }}
                  title="90°"
                >
                  <span style={{ transform: "rotate(90deg)" }}>↻</span>
                </button>
                <button
                  className={`btn border d-flex align-items-center justify-content-center ${
                    (logoEditingState.rotation || 0) === 180
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => onRotationChange?.(180)}
                  style={{ width: "24px", height: "24px", fontSize: "10px" }}
                  title="180°"
                >
                  <span style={{ transform: "rotate(180deg)" }}>↻</span>
                </button>
                <button
                  className={`btn border d-flex align-items-center justify-content-center ${
                    (logoEditingState.rotation || 0) === 270
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => onRotationChange?.(270)}
                  style={{ width: "24px", height: "24px", fontSize: "10px" }}
                  title="270°"
                >
                  <span style={{ transform: "rotate(270deg)" }}>↻</span>
                </button>
              </div>

              <input
                type="range"
                className="form-range"
                min="-360"
                max="360"
                value={logoEditingState.rotation || 0}
                onChange={(e) => {
                  const newRotation = Number(e.target.value);
                  if (
                    !isNaN(newRotation) &&
                    newRotation !== null &&
                    newRotation !== undefined
                  ) {
                    onRotationChange?.(newRotation);
                  }
                }}
                style={{ width: "100px", height: "18px" }}
              />
              <div className="small text-center mt-1">
                {logoEditingState.rotation || 0}°
              </div>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center">
          <input
            type="checkbox"
            className="form-check-input me-1"
            checked={maintainAspectRatio}
            onChange={(e) => {
              onAspectRatioToggle?.(e.target.checked);
            }}
          />
          <span className="small">Lock Ratio</span>
        </div>

        <button
          className="btn btn-danger btn-sm text-capitalize"
          onClick={onRemove}
          title="Remove Logo"
        >
          Remove
        </button>
      </>
    );
  };

  return (
    <div ref={modalRef} onClick={(e) => e.stopPropagation()}>
      <div
        className="d-flex align-items-center w-100 px-2 py-1"
        style={{ gap: "6px", height: "38px" }}
      >
        {renderTextControls()}
        {renderLogoControls()}
      </div>
    </div>
  );
};

export const TextEditingModal = OverlayEditingModal;
